import type { FunctionalComponent as FC } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { Button } from '@vkontakte/vkui';

import { useVkCaptchaContext } from '../../context/VkCaptchaContext';
import { useWidget } from '../../context/WidgetContext';
import { useMessageContent } from '../../hooks/useMessageContent';
import { useMessageJson } from '../../hooks/useMessageJson';
import type { MessageJsonButtons, MessageWithButtons as Props } from '../../types/messages';
import type { WebSocketMessageCSATButton } from '../../types/ws-events';
import { WebsocketEvents, WebsocketMessageType } from '../../types/ws-events';
import { getResponseMessageContentType } from '../../utils';
import { useStompClient } from 'react-stomp-hooks';

import { MessageBubble } from '../MessageBubble';

import styles from './styles.module.css';

export const MessageWithButtons: FC<Props> = ({ buttonsDisabled, ...messageProps }) => {
  const { messageContentType, messageJson, id } = messageProps;

  const { setMessages } = useWidget();
  const stompClient = useStompClient();
  const { handleCaptchaOpen, isCaptchaPassed } = useVkCaptchaContext();
  const { messageButtons, messageJsonSelectedValue } = useMessageJson(messageJson);
  const { message, messageHtml } = useMessageContent(messageProps);

  const [disabled, setDisabled] = useState(buttonsDisabled || Boolean(messageJsonSelectedValue));
  const messageBubbleRef = useRef<HTMLDivElement>(null);

  const [pendingButton, setPendingButton] = useState<WebSocketMessageCSATButton | null>(null);

  const handleSocketPublish = useCallback(
    (button: WebSocketMessageCSATButton) => {
      if (stompClient) {
        stompClient.publish({
          destination: WebsocketEvents.AppMessage,
          body: JSON.stringify({
            messageContentType: getResponseMessageContentType(messageContentType),
            messageJson: { value: button.value },
            message: button.text,
          }),
        });
      }

      setDisabled(true);

      setMessages?.((prevMessages) => {
        return prevMessages.map((message) =>
          message.id === id
            ? {
                ...message,
                messageJson: {
                  ...(message.messageJson as MessageJsonButtons), // точно знаем, что тут кнопки
                  selectedValue: button.value,
                },
              }
            : message
        );
      });
    },
    [id, messageContentType, setMessages, stompClient]
  );

  const handleButtonClick = useCallback(
    (button: WebSocketMessageCSATButton) => {
      return () => {
        if (isCaptchaPassed) {
          // Отправляем сразу, если капча пройдена
          handleSocketPublish(button);
        } else {
          // Добавляем кнопку в очередь, чтобы отправить сразу после прохождения капчи
          setPendingButton(button);

          // Открываем капчу для прохождения
          handleCaptchaOpen();
        }
      };
    },
    [handleCaptchaOpen, handleSocketPublish, isCaptchaPassed]
  );

  /** Формирование подвала для сообщения с кнопками */
  const messageFooter = useMemo(() => {
    if (!messageButtons || !messageButtons.length) {
      return null;
    }

    return (
      <div className={styles.csatButtons} data-id={id}>
        {messageButtons.flat().map((button, index) => (
          <Button
            mode="primary"
            size="s"
            appearance="overlay"
            key={`csat-${index}`}
            onClick={handleButtonClick(button)}
            disabled={disabled}
          >
            {button.text}
          </Button>
        ))}
      </div>
    );
  }, [disabled, handleButtonClick, id, messageButtons]);

  // Отслеживаем состояние прохождения капчи и наличия неотправленных кнопок
  useEffect(() => {
    if (isCaptchaPassed && pendingButton !== null && stompClient) {
      handleSocketPublish(pendingButton);
      setPendingButton(null);
    }
  }, [handleSocketPublish, isCaptchaPassed, pendingButton, stompClient]);

  // Сбросит disabled состояние для нового блока
  useEffect(() => {
    setDisabled(buttonsDisabled || Boolean(messageJsonSelectedValue));
  }, [buttonsDisabled, messageJsonSelectedValue]);

  return (
    <div ref={messageBubbleRef}>
      <MessageBubble
        {...messageProps}
        message={message}
        messageHtml={messageHtml}
        messageType={WebsocketMessageType.OutSystem}
        messageFooter={messageFooter}
      />
    </div>
  );
};
